import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileCheck2, Plus, ShieldAlert } from 'lucide-react';
import { Button, Card, DocumentStatusBadge, EmptyState, ErrorState, Notice, Skeleton } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { api, errorMessage } from '../../../lib/api';
import { useAuth } from '../../../stores/auth';
import { daysLeftLabel, type Compliance, type DocumentDto } from './types';

export function ComplianceBanner() {
  const isDriver = useAuth((s) => s.hasRole('DRIVER'));
  const c = useQuery({ queryKey: ['compliance'], queryFn: () => api<Compliance>('/documents/compliance'), enabled: isDriver });
  if (!c.data) return null;
  // Socios: sin los obligatorios subidos y verificados no pueden cotizar ni iniciar viajes.
  if (c.data.required && (c.data.notReady?.length || !c.data.hasVehicle)) {
    const label: Record<string, string> = { MISSING: 'falta subirlo', PENDING_VERIFICATION: 'en revisión', REJECTED: 'rechazado, súbelo de nuevo', EXPIRED: 'vencido' };
    return (
      <Notice tone="danger">
        <strong className="flex items-center gap-2"><ShieldAlert className="size-4" aria-hidden /> Aún no puedes recibir viajes</strong>
        Para cotizar e iniciar viajes necesitas {c.data.hasVehicle ? 'tus documentos obligatorios verificados por la administración' : 'registrar tu vehículo y subir tus documentos obligatorios'}:
        <ul className="mt-1 list-disc pl-5">
          {!c.data.hasVehicle && <li>Vehículo: falta registrarlo</li>}
          {c.data.notReady?.map((e) => <li key={e.documentType + (e.vehicle ?? '')}>{e.documentType}{e.vehicle ? ` (${e.vehicle})` : ''}: {label[e.status] ?? e.status}</li>)}
        </ul>
      </Notice>
    );
  }
  if (!c.data.ok)
    return (
      <Notice tone="danger">
        <strong className="flex items-center gap-2"><ShieldAlert className="size-4" aria-hidden /> Documentos vencidos</strong>
        {c.data.expired.map((e) => e.documentType + (e.vehicle ? ` (${e.vehicle})` : '')).join(', ')}. Según la configuración del negocio, puedes tener bloqueadas nuevas operaciones.
      </Notice>
    );
  const missing = c.data.warnings.filter((w) => w.status === 'MISSING');
  if (missing.length) return <Notice tone="warning">Falta registrar: {missing.map((m) => m.documentType).join(', ')}.</Notice>;
  return null;
}

export function DocumentsPage() {
  const isAdmin = useAuth((s) => s.hasRole('TENANT_ADMIN'));
  const q = useQuery({ queryKey: ['documents'], queryFn: () => api<DocumentDto[]>('/documents') });
  const active = q.data?.filter((d) => !d.superseded) ?? [];
  const history = q.data?.filter((d) => d.superseded) ?? [];

  return (
    <Page
      title="Mis documentos"
      back="/driver/more"
      onRefresh={() => q.refetch()}
      actions={<Link to="/driver/documents/new" aria-label="Agregar documento" className="tap flex items-center justify-center px-2"><Plus className="size-6" /></Link>}
    >
      <div className="flex flex-col gap-4">
        <ComplianceBanner />
        {isAdmin && <Notice>Como administrador verás también los documentos de otros conductores del negocio. Las imágenes nunca se muestran a clientes.</Notice>}
        {q.isLoading && <Skeleton className="h-28" />}
        {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
        {q.data && active.length === 0 && (
          <EmptyState
            icon={<FileCheck2 className="size-6" />}
            title="Sin documentos registrados"
            description="Registra tu licencia de conducir y el seguro de tu vehículo."
            action={<Link to="/driver/documents/new"><Button>Agregar documento</Button></Link>}
          />
        )}
        {active.map((d) => (
          <Link key={d.id} to={`/driver/documents/${d.id}`}>
            <Card className={`p-4 border-l-4 ${d.status === 'EXPIRED' || d.status === 'REJECTED' ? 'border-l-brand-red' : d.status === 'EXPIRING_SOON' ? 'border-l-amber-500' : d.status === 'VALID' ? 'border-l-emerald-500' : 'border-l-sky-500'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold">{d.type.name}</h3>
                  <p className="text-sm text-[var(--text-muted)] truncate">
                    {[d.vehicle?.label, d.number, isAdmin ? d.driver.name : null].filter(Boolean).join(' · ') || 'Sin número'}
                  </p>
                </div>
                <DocumentStatusBadge status={d.status} />
              </div>
              {d.expiresAt && (
                <p className={`mt-2 text-sm ${d.daysLeft !== null && d.daysLeft <= 7 ? 'text-brand-red font-medium' : 'text-[var(--text-muted)]'}`}>
                  {daysLeftLabel(d.daysLeft)} · {new Date(d.expiresAt + 'T12:00:00').toLocaleDateString('es-DO', { dateStyle: 'medium' })}
                </p>
              )}
            </Card>
          </Link>
        ))}
        {history.length > 0 && (
          <details className="mt-2">
            <summary className="tap cursor-pointer text-sm text-[var(--text-muted)]">Historial ({history.length})</summary>
            <div className="mt-2 flex flex-col gap-2">
              {history.map((d) => (
                <Link key={d.id} to={`/driver/documents/${d.id}`} className="text-sm px-2 py-2 rounded-xl hover:bg-black/5">
                  {d.type.name} {d.number ? `· ${d.number}` : ''} · reemplazado
                </Link>
              ))}
            </div>
          </details>
        )}
      </div>
    </Page>
  );
}
